import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Stack } from '@mui/material';
import HookFormCheckbox from 'src/components/hookform/HookFormCheckbox';
import HookFormTextField from 'src/components/hookform/HookFormTextField';
import BackButton from 'src/components/BackButton';
import SuccessButton from 'src/components/SuccessButton';
import { getBasePolicyModeOptions } from 'src/utils/enums/basePolicyMode';
import { ENVIRONMENT_DEFAULT_HELP } from './EditManagedBasePolicyForm';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const VERSION_REGEX = /^\d+(\.\d+){1,3}$/;

// Registering a base policy by hand is only worth doing in order to generate against it, so managed
// is not a choice. The field is here to say so rather than to be answered.
const MANAGED_OPTIONS = [{ value: 'managed', label: 'Managed by the portal' }];

const schema = Yup.object().shape({
  policyName: Yup.string().trim().required('Policy name is required'),
  policyId: Yup.string()
    .trim()
    .required('Policy ID is required')
    .matches(GUID_REGEX, 'Must be a valid GUID: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'),
  policyDescription: Yup.string().trim(),
  basePolicyMode: Yup.string().required('Base policy mode is required'),
  policyVersion: Yup.string()
    .trim()
    .required('Version is required')
    .matches(VERSION_REGEX, 'Use a numeric version like 1.0.0.0'),
  environmentDefault: Yup.boolean(),
});

const DEFAULT_VALUES = {
  policyName: '',
  policyId: '',
  policyDescription: '',
  basePolicyMode: 'Enforce',
  policyVersion: '1.0.0.0',
  managed: 'managed',
  environmentDefault: false,
};

const AddManagedBasePolicyForm = ({ loading, onCancel, onSubmit }) => {
  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = formMethods;

  const submit = values =>
    onSubmit({
      policyName: values.policyName.trim(),
      policyId: values.policyId.trim(),
      policyDescription: values.policyDescription?.trim() || undefined,
      basePolicyMode: values.basePolicyMode,
      policyVersion: values.policyVersion.trim(),
      environmentDefault: values.environmentDefault,
    });

  return (
    <form autoComplete="off" onSubmit={handleSubmit(submit)}>
      <Stack spacing={2}>
        <HookFormTextField
          name="policyName"
          label="Policy name"
          formMethods={formMethods}
          size="small"
          fullWidth
        />
        <HookFormTextField
          name="policyId"
          label="Policy ID"
          formMethods={formMethods}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          size="small"
          fullWidth
        />
        <HookFormTextField
          name="policyDescription"
          label="Policy description"
          formMethods={formMethods}
          placeholder="Optional"
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
        />
        <HookFormTextField
          name="basePolicyMode"
          label="Base policy mode"
          formMethods={formMethods}
          select
          size="small"
          fullWidth
          options={getBasePolicyModeOptions()}
        />
        <HookFormTextField
          name="policyVersion"
          label="Policy version"
          formMethods={formMethods}
          size="small"
          fullWidth
        />
        <HookFormTextField
          name="managed"
          label="Managed"
          formMethods={formMethods}
          select
          disabled
          size="small"
          fullWidth
          options={MANAGED_OPTIONS}
        />
        <HookFormCheckbox
          name="environmentDefault"
          label="Environment default"
          helperText={ENVIRONMENT_DEFAULT_HELP}
          formMethods={formMethods}
        />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <BackButton disabled={loading} onClick={onCancel}>
            Cancel
          </BackButton>
          <SuccessButton type="submit" disabled={loading || !isValid} loading={loading}>
            Add and Continue
          </SuccessButton>
        </Stack>
      </Stack>
    </form>
  );
};

AddManagedBasePolicyForm.propTypes = {
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default AddManagedBasePolicyForm;
